// EXP1-S1 (docs/build-briefs/EXP1-S1-export-pipeline-csv-xlsx.md; DEC-402): the one Export menu every report screen
// shares. It wraps an EXISTING trigger button (so its visibility, label and permission gating stay where they were) and,
// when the screen says the menu applies, opens a short list of formats under it; otherwise a tap runs the screen's plain
// handler, exactly as before. Each item is at least the touch minimum tall, and the menu closes on a choice, on Escape
// and on a tap anywhere else. No title attribute: the labels are visible.

import {
  EXPORT_FORMAT_CHOICES,
  type ExportFormat,
  type GateExportFormat,
} from "./exportMenuModel.ts";
import {
  ensureGoogleAccessToken,
  googleSheetsEnabled,
  loadGoogleSheetsConfig,
  preloadGoogleIdentity,
} from "./googleSheets.ts";
import { GOOGLE_SECONDARY_LINE } from "./googleSheetsModel.ts";

/** What the menu needs to ask whether Google is configured. A location screen names its tenant; the franchisor console, which
 * has none (PAY2-S8), names its own session-scoped options read instead. */
export interface GoogleSheetSession {
  apiBaseUrl: string;
  tenantId: string;
  loadOptions?: () => Promise<{
    status: number;
    body: { googleSheetsClientId?: string | null } | null;
  }>;
}

interface ExportMenuBase {
  trigger: HTMLButtonElement;
  /** Items get `data-testid="<prefix>-<format>"`, the menu `<prefix>-menu`. */
  testIdPrefix: string;
  /** True when a tap on the trigger should open the menu; false runs `onPlain`. */
  menuActive: () => boolean;
  onPlain: () => void;
  /** EXP1-S3: a report that was several CSV files offers one CSV per sheet. Excel and PDF are always the whole report.
   * Read each time the menu opens, so it follows the report on screen. Fewer than two entries is the plain CSV item. */
  sheetChoices?: () => readonly { id: string; label: string }[];
}

/** EXP1-S6: a location report screen opts into the Google Sheet item with `googleSheet`, which names the signed-in
 * session (for the one question to our server about whether Google is configured). The item shows only when a client id
 * is configured; without one the menu is exactly what it was. A screen that does not opt in (the franchisor and Software
 * Owner consoles) only ever receives the three file formats. */
export type ExportMenuOptions = ExportMenuBase &
  (
    | { googleSheet?: undefined; onPick: (format: ExportFormat, sheetId?: string) => void }
    | {
        googleSheet: () => GoogleSheetSession | undefined;
        onPick: (format: GateExportFormat, sheetId?: string) => void;
      }
  );

export interface ExportMenu {
  close: () => void;
}

export function createExportMenu(
  options: ExportMenuBase & {
    googleSheet?: undefined;
    onPick: (format: ExportFormat, sheetId?: string) => void;
  },
): ExportMenu;
export function createExportMenu(
  options: ExportMenuBase & {
    googleSheet: () => GoogleSheetSession | undefined;
    onPick: (format: GateExportFormat, sheetId?: string) => void;
  },
): ExportMenu;
export function createExportMenu(options: ExportMenuOptions): ExportMenu {
  const { trigger } = options;
  const googleOptions = options.googleSheet === undefined ? undefined : options;
  const wrap = document.createElement("span");
  wrap.className = "export-menu";
  trigger.before(wrap);
  wrap.append(trigger);

  const list = document.createElement("div");
  list.className = "export-menu-list";
  list.setAttribute("role", "menu");
  list.dataset["testid"] = `${options.testIdPrefix}-menu`;
  list.hidden = true;
  wrap.append(list);

  // EXP1-S6: one plain line for a Google sign-in that did not finish, shown under the trigger until the next tap.
  const note = document.createElement("p");
  note.className = "export-menu-note";
  note.setAttribute("role", "status");
  note.dataset["testid"] = `${options.testIdPrefix}-note`;
  note.hidden = true;
  wrap.append(note);

  trigger.setAttribute("aria-haspopup", "menu");
  trigger.setAttribute("aria-expanded", "false");

  function addItem(testId: string, label: string, format: ExportFormat, sheetId?: string): void {
    const item = document.createElement("button");
    item.type = "button";
    item.className = "export-menu-item";
    item.setAttribute("role", "menuitem");
    item.dataset["testid"] = testId;
    item.textContent = label;
    item.addEventListener("click", () => {
      close();
      options.onPick(format, sheetId);
    });
    list.append(item);
  }

  function addGoogleItem(): void {
    const item = document.createElement("button");
    item.type = "button";
    item.className = "export-menu-item export-menu-item-google";
    item.setAttribute("role", "menuitem");
    item.dataset["testid"] = `${options.testIdPrefix}-google_sheet`;
    const label = document.createElement("span");
    label.textContent = "Google Sheet";
    const secondary = document.createElement("span");
    secondary.className = "export-menu-item-sub";
    secondary.textContent = GOOGLE_SECONDARY_LINE;
    item.append(label, secondary);
    item.addEventListener("click", () => {
      close();
      // The Google sign-in is requested inside this tap, so the browser allows its window. Only a held token lets the
      // screen run its export, so a cancelled sign-in calls our server for nothing and audits nothing.
      const access = ensureGoogleAccessToken();
      void access.then((outcome) => {
        if (outcome.ok) {
          googleOptions?.onPick("google_sheet");
        } else {
          note.textContent = outcome.message;
          note.hidden = false;
        }
      });
    });
    list.append(item);
  }

  /** Rebuilt on every open: the sheets on offer belong to the report on screen. */
  function renderItems(): void {
    list.replaceChildren();
    const sheets = options.sheetChoices?.() ?? [];
    for (const choice of EXPORT_FORMAT_CHOICES) {
      if (choice.format === "csv" && sheets.length > 1) {
        sheets.forEach((sheet, index) => {
          addItem(
            index === 0 ? `${options.testIdPrefix}-csv` : `${options.testIdPrefix}-csv-${sheet.id}`,
            `${choice.label}: ${sheet.label}`,
            "csv",
            sheet.id,
          );
        });
      } else {
        addItem(`${options.testIdPrefix}-${choice.format}`, choice.label, choice.format);
      }
    }
    if (googleOptions !== undefined && googleSheetsEnabled()) {
      addGoogleItem();
    }
  }
  function close(): void {
    list.hidden = true;
    trigger.setAttribute("aria-expanded", "false");
  }

  function open(): void {
    note.hidden = true;
    note.textContent = "";
    renderItems();
    list.hidden = false;
    trigger.setAttribute("aria-expanded", "true");
    const session = googleOptions?.googleSheet();
    if (session !== undefined) {
      // The item appears once the server has said Google is configured; the script loads so the tap can use it at once.
      void loadGoogleSheetsConfig(session.apiBaseUrl, session.tenantId, session.loadOptions).then(
        () => {
          if (googleSheetsEnabled()) {
            void preloadGoogleIdentity();
            if (!list.hidden) renderItems();
          }
        },
      );
    }
  }

  trigger.addEventListener("click", () => {
    if (!options.menuActive()) {
      close();
      options.onPlain();
      return;
    }
    if (list.hidden) {
      open();
    } else {
      close();
    }
  });

  // A menu inside a card that is redrawn is gone from the page; its document listeners remove themselves then.
  const onPointerDown = (event: PointerEvent): void => {
    if (!wrap.isConnected) {
      document.removeEventListener("pointerdown", onPointerDown);
      document.removeEventListener("keydown", onKeyDown);
      return;
    }
    if (!list.hidden && event.target instanceof Node && !wrap.contains(event.target)) {
      close();
    }
  };
  const onKeyDown = (event: KeyboardEvent): void => {
    if (event.key === "Escape" && !list.hidden) {
      close();
      trigger.focus();
    }
  };
  document.addEventListener("pointerdown", onPointerDown);
  document.addEventListener("keydown", onKeyDown);

  return { close };
}
